import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateScene } from '../../../domains/scene/ui/hooks/useCreateScene';
import {
  SCENE_READY_POLL_INTERVAL_MS,
  useGetSceneDetail
} from '../../../domains/scene/ui/hooks/useGetSceneDetail';
import { LOCAL_STORAGE_KEYS } from '../constants/localStorageKeys';
import type { components } from '../../../types/schema';

type AdaptedFictionPreview = components['schemas']['ApiV1AdaptedFictionPreview'];

export const useManageCreateScene = (preview: AdaptedFictionPreview) => {
  const navigate = useNavigate();
  const [pendingSceneId, setPendingSceneId] = useState<string | null>(null);
  const castRelationshipId =
    localStorage.getItem(LOCAL_STORAGE_KEYS.CURRENT_CAST_RELATIONSHIP_ID) ?? '';

  const { mutateAsync: createScene } = useCreateScene();
  const { data: sceneDetail } = useGetSceneDetail(pendingSceneId ?? '', {
    enabled: !!pendingSceneId,
    refetchInterval: pendingSceneId ? SCENE_READY_POLL_INTERVAL_MS : false
  });

  useEffect(() => {
    if (
      pendingSceneId &&
      (sceneDetail?.status === 'primary_ready' || sceneDetail?.status === 'completed')
    ) {
      setPendingSceneId(null);
      navigate(`/scene/${pendingSceneId}/preview`);
    }
  }, [pendingSceneId, sceneDetail?.status, navigate]);

  const handleCreateScene = async () => {
    if (!castRelationshipId) return;
    if (preview?.sceneId) {
      navigate(
        `/scene/${preview.sceneId}/preview${preview?.lastContentId ? `?cursor=${preview?.lastContentId}` : ''}`
      );
      return;
    }

    const requestBody = {
      scene: {
        cast_relationship_id: castRelationshipId,
        adapted_fiction_preview_id: preview.adaptedFictionPreviewId
      }
    };
    const response = await createScene(requestBody);
    if (response?.sceneId) {
      setPendingSceneId(response.sceneId);
    }
  };

  return {
    pendingSceneId,
    handleCreateScene
  };
};
